import { Button } from '@/components/common/button';

import styles from './import-action-bar.module.scss';

export type ImportActionBarProps = {
  /**
   * Whether **Continue** is unavailable. `false` here and always on the single-shape preview, where
   * the one shape is selected on arrival (grilling L5 — single shape is the degenerate case of
   * sticky-until-replaced, which is what Figma `1657:789257` draws). Ticket 04 drives it from
   * `Continue enabled ⟺ a selectable shape is selected`.
   */
  continueDisabled?: boolean;
  /**
   * The bar's leading label. `null` in **Placement mode**, which draws none — the "Field Preview"
   * label belongs to the preview step, and the placement step has no Figma frame of its own
   * (flagged to the BA as BA-1).
   */
  label?: string | null;
  /** Names the `role="group"`, so the two modes' Cancel buttons are separately addressable. */
  groupLabel?: string;
  /** The confirm button's label — `"Place Field"` in Placement mode. Its TREATMENT does not change
   *  with it: the criterion replaces the word, and Figma `1657:790438`'s highlight button is the
   *  position it occupies. */
  confirmLabel?: string;
  onCancel: () => void;
  onContinue: () => void;
};

/**
 * The **Import preview**'s foot bar (Figma `1657:790434`): the "Field Preview" label at one end,
 * Cancel and Continue at the other.
 *
 * Its own component rather than JSX in the page, for the reason grilling Q2 imposed on this whole
 * surface — `<MapPage>` reached 933 lines and this page must not go the same way — and because
 * **three** tickets touch this bar: 04 drives `continueDisabled`, 05 replaces the control set with
 * placement's.
 *
 * `aria-disabled`, not `disabled`, on Continue: it matches `<FieldMappingPage>`'s foot bar, keeps
 * the control in the tab order and in any `querySelectorAll('button')` walk, and leaves room for
 * ticket 04 to *say why* on a press rather than swallow it silently.
 */
export function ImportActionBar({
  label = 'Field Preview',
  groupLabel = 'Field Preview actions',
  confirmLabel = 'Continue',
  continueDisabled = false,
  onCancel,
  onContinue,
}: ImportActionBarProps) {
  return (
    <div className={styles.footBar} role="group" aria-label={groupLabel}>
      {label === null ? null : <p className={styles.label}>{label}</p>}
      <div className={styles.actions}>
        <Button variant="secondary" size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="highlight"
          size="small"
          aria-disabled={continueDisabled}
          onClick={continueDisabled ? undefined : onContinue}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
